import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Navigation, RefreshCw, Sparkles, Wallet } from 'lucide-react';
import { Button, BudgetPill, Card, DataBadge, EmptyState, cn } from '@tappa/ui';
import type { ItineraryItemSummary, TripDayDetail, TripSummary } from '@tappa/shared';
import { describeDayBudget, formatDuration } from '@tappa/shared';
import { ApiError, api, freshnessApi } from '../lib/api.js';
import type { MeteoGiorno } from '../lib/api.js';
import { useGeolocation } from '../lib/geolocation.js';
import { openNavigation } from '../lib/navigation.js';
import { useAuth } from '../lib/auth.js';
import { useCurrentTrip } from '../lib/currentTrip.js';
import { Timeline } from '../features/itinerary/Timeline.js';
import { PlaceBanner } from '../features/places/PlaceImage.js';
import { AddActivity } from '../features/itinerary/AddActivity.js';
import { DayHours } from '../features/itinerary/DayHours.js';

/**
 * Schermata "Oggi".
 *
 * La domanda a cui risponde e' una sola: COSA FACCIO ADESSO?
 * La card della prossima azione domina lo schermo; tutto il resto sta sotto.
 */
export function TodayPage(): ReactNode {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { trip, detail, isLoading } = useCurrentTrip();
  const { position } = useGeolocation();
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const todayQuery = useQuery({
    queryKey: ['today', trip?.id],
    queryFn: () => api.trips.today(trip?.id ?? ''),
    enabled: trip !== null,
    refetchInterval: 60_000,
  });

  const overview = todayQuery.data?.overview ?? null;

  // Il giorno mostrato: quello scelto dall'utente, altrimenti quello di oggi.
  useEffect(() => {
    if (selectedDayId === null && overview?.day != null) setSelectedDayId(overview.day.id);
  }, [overview, selectedDayId]);

  // Il meteo sta su una query separata: se Open-Meteo non risponde, la giornata
  // si apre lo stesso. È un di più utile, non una dipendenza.
  const weatherQuery = useQuery({
    queryKey: ['weather', trip?.id],
    queryFn: () => api.weather(trip?.id ?? ''),
    enabled: trip !== null,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const meteo = weatherQuery.data?.days ?? {};

  const dayQuery = useQuery({
    queryKey: ['day', trip?.id, selectedDayId],
    queryFn: () => api.days.detail(trip?.id ?? '', selectedDayId ?? ''),
    enabled: trip !== null && selectedDayId !== null,
  });

  const day = dayQuery.data?.day ?? overview?.day ?? null;

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['today', trip?.id] }),
      queryClient.invalidateQueries({ queryKey: ['day', trip?.id] }),
      queryClient.invalidateQueries({ queryKey: ['budget', trip?.id] }),
    ]);
  };

  const generate = useMutation({
    mutationFn: () => api.days.generate(trip?.id ?? '', selectedDayId ?? '', true),
    onSuccess: async (result) => {
      setNotice(
        result.rejected.length === 0
          ? result.message
          : `${result.message} Ho escluso: ${result.rejected.map((r) => `${r.name} (${r.reason})`).join(', ')}.`,
      );
      await invalidate();
    },
    onError: (error) => {
      setNotice(error instanceof ApiError ? error.message : 'Generazione non riuscita.');
    },
  });

  const moveItem = useMutation({
    mutationFn: async (input: { itemId: string; direction: -1 | 1 }) => {
      if (day === null) return;
      const ids = day.items.map((item) => item.id);
      const from = ids.indexOf(input.itemId);
      const to = from + input.direction;
      if (from < 0 || to < 0 || to >= ids.length) return;
      const next = [...ids];
      const [moved] = next.splice(from, 1);
      if (moved !== undefined) next.splice(to, 0, moved);
      await api.items.reorder(trip?.id ?? '', day.id, next);
    },
    onSuccess: invalidate,
  });

  const toggleLock = useMutation({
    mutationFn: (item: ItineraryItemSummary) =>
      api.items.update(trip?.id ?? '', item.id, { isLocked: !item.isLocked }),
    onSuccess: invalidate,
    onError: (error) => {
      setNotice(error instanceof ApiError ? error.message : 'Modifica non riuscita.');
    },
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.items.remove(trip?.id ?? '', itemId),
    onSuccess: invalidate,
  });

  /**
   * Rilettura di orari e prezzi dei luoghi di oggi.
   * Il gesto giusto prima di uscire: sono i dati che invecchiano peggio.
   */
  const refreshDay = useMutation({
    mutationFn: () => freshnessApi.refreshDay(trip?.id ?? '', selectedDayId ?? ''),
    onSuccess: async (result) => {
      setNotice(result.message);
      await invalidate();
    },
    onError: (error) => {
      setNotice(error instanceof ApiError ? error.message : 'Aggiornamento non riuscito.');
    },
  });

  if (isLoading) return <TodaySkeleton />;

  if (trip === null) {
    return (
      <EmptyState
        icon={<CalendarPlus size={44} />}
        title="Dove andiamo? ✈️"
        description="Dimmi città e date: da lì costruisco le giornate, con orari, spostamenti e budget."
        action={
          <Link to="/nuovo-viaggio">
            <Button size="lg">Crea il tuo primo viaggio</Button>
          </Link>
        }
      />
    );
  }

  const timezone = trip.destination.timezone;
  const canEdit = trip.role !== 'viewer';

  return (
    <div className="flex flex-col gap-4">
      <header>
        <p className="text-[15px] text-text-muted">
          {greeting()} {auth.user?.displayName ?? ''} 👋
        </p>
        <h1 className="font-display text-[26px] leading-8 text-text">{trip.name}</h1>
        <p className="text-[14px] text-text-muted">
          {trip.destination.name} · fuso {timezone}
          {overview !== null ? ` · ${overview.localTime ?? ''} sul posto` : ''}
        </p>
      </header>

      {overview !== null ? (
        <NextActionCard overview={overview} trip={trip} position={position} />
      ) : null}

      {overview !== null && overview.alerts.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {overview.alerts.map((alert) => (
            <li
              key={alert.code + alert.message}
              className={cn(
                'rounded-[var(--r-md)] px-3 py-2 text-[14px]',
                alert.severity === 'error'
                  ? 'bg-[var(--budget-blocked-soft)] text-[var(--budget-blocked)]'
                  : 'bg-[var(--budget-warn-soft)] text-[var(--budget-warn)]',
              )}
            >
              {alert.message}
            </li>
          ))}
        </ul>
      ) : null}

      {detail !== null && detail.days.length > 0 ? (
        <DayTabs
          days={detail.days}
          meteo={meteo}
          selectedId={selectedDayId}
          onSelect={(id) => {
            setSelectedDayId(id);
            setNotice(null);
          }}
        />
      ) : null}

      {day !== null ? (
        <Card>
          <div className="mb-1 flex items-center justify-between gap-2">
            <h2 className="font-display text-[18px] text-text">La giornata</h2>
            {day.plannedCostMinor > 0 || day.actualCostMinor > 0 ? (
              <BudgetPill level={day.budgetLevel} />
            ) : null}
          </div>

          {/* Meteo del giorno aperto. Quando manca, si dice perché. */}
          <MeteoDelGiorno
            meteo={meteo[day.date]}
            disponibile={weatherQuery.data?.available ?? false}
            giorniPrevisione={weatherQuery.data?.forecastDays ?? 16}
          />

          {/* Gli orari di QUESTA giornata: sono quelli che il planner legge. */}
          <div className="mb-3">
            <DayHours
              tripId={trip.id}
              dayId={day.id}
              inizio={day.dayStartLocal}
              fine={day.dayEndLocal}
              canEdit={canEdit}
            />
          </div>

          {day.items.length === 0 ? (
            <div className="py-4 text-center">
              <p className="mb-3 text-[15px] text-text-muted">
                Questa giornata è ancora vuota.
              </p>
              {canEdit ? (
                <Button
                  iconLeft={<Sparkles size={18} />}
                  loading={generate.isPending}
                  onClick={() => {
                    generate.mutate();
                  }}
                >
                  Costruiscila per me
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              <Timeline
                base={day.base}
                items={day.items}
                legs={day.legs}
                timezone={timezone}
                currency={trip.baseCurrency}
                canEdit={canEdit}
                onMove={(itemId, direction) => {
                  moveItem.mutate({ itemId, direction });
                }}
                onToggleLock={(item) => {
                  toggleLock.mutate(item);
                }}
                onRemove={(itemId) => {
                  removeItem.mutate(itemId);
                }}
              />
              {canEdit ? (
                <div className="mt-3 flex flex-col gap-3">
                  <AddActivity
                    tripId={trip.id}
                    tripDayId={day.id}
                    dataGiornata={day.date}
                    timezone={timezone}
                  />
                </div>
              ) : null}

              {canEdit ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    iconLeft={<Sparkles size={18} />}
                    loading={generate.isPending}
                    onClick={() => {
                      generate.mutate();
                    }}
                  >
                    Rigenera giornata
                  </Button>
                  <Link to="/scopri">
                    <Button variant="ghost">Aggiungi una tappa</Button>
                  </Link>
                  <Button
                    variant="ghost"
                    iconLeft={<RefreshCw size={18} />}
                    loading={refreshDay.isPending}
                    onClick={() => {
                      refreshDay.mutate();
                    }}
                  >
                    Aggiorna orari e prezzi
                  </Button>
                </div>
              ) : null}
            </>
          )}

          {notice !== null ? (
            <p role="status" className="mt-3 rounded-[var(--r-md)] bg-surface-sunken px-3 py-2 text-[13.5px] text-text-muted">
              {notice}
            </p>
          ) : null}
        </Card>
      ) : null}

      {overview?.budget != null ? (
        <Card tone="sunken">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-text-muted" aria-hidden="true" />
            <p className="text-[15px] text-text">
              {describeDayBudget({
                spentMinor: overview.budget.spentMinor,
                limitMinor: overview.budget.limitMinor,
                formatAmount: (minor) =>
                  new Intl.NumberFormat('it-IT', {
                    style: 'currency',
                    currency: overview.budget?.currency ?? 'EUR',
                  }).format(minor / 100),
              })}
            </p>
          </div>
        </Card>
      ) : null}

      {overview !== null && overview.missingCount > 0 ? (
        <Link to="/viaggio" className="block">
          <Card>
            <p className="text-[15px] text-text">
              Ti mancano ancora <strong>{overview.missingCount}</strong> cose da sistemare prima di
              partire.
            </p>
            <p className="mt-1 text-[13.5px] text-text-muted">Vedi la checklist →</p>
          </Card>
        </Link>
      ) : null}
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buongiorno';
  if (hour < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}

/**
 * La card piu' importante dell'app.
 * Durante il viaggio domina la schermata: orario, tempo di percorrenza e
 * soprattutto QUANDO PARTIRE.
 */
function NextActionCard({
  overview,
  trip,
  position,
}: {
  overview: NonNullable<Awaited<ReturnType<typeof api.trips.today>>['overview']>;
  trip: TripSummary;
  position: { lat: number; lon: number } | null;
}): ReactNode {
  const { nextAction } = overview;
  const item = nextAction.item;

  if (item === null) {
    return (
      <Card tone="brand">
        <p className="text-[15px] text-text">{nextAction.message}</p>
      </Card>
    );
  }

  const time =
    item.plannedStart === null
      ? '--:--'
      : new Date(item.plannedStart).toLocaleTimeString('it-IT', {
          timeZone: trip.destination.timezone,
          hour: '2-digit',
          minute: '2-digit',
        });



  return (
    <Card tone="brand" className="border-brand/20">
      <p className="text-[13.5px] font-medium uppercase tracking-wide text-brand-strong">
        {overview.mode === 'travelling' ? 'Prossima tappa' : 'Si comincia da qui'}
      </p>

      <h2 className="mt-1 font-display text-[22px] leading-7 text-text">{item.title}</h2>

      {/* Vedere il posto prima di arrivarci: si riconosce da lontano. */}
      {item.imageUrl !== null ? (
        <div className="mt-2">
          <PlaceBanner
            url={item.imageUrl}
            name={item.title}
            attribution={item.imageAttribution}
          />
        </div>
      ) : null}

      <p className="tabular mt-1 font-display text-[32px] leading-9 text-text">{time}</p>

      {nextAction.leg !== null ? (
        <p className="mt-1 text-[15px] text-text-muted">
          {formatDuration(nextAction.leg.durationSec)} ·{' '}
          {(nextAction.leg.distanceM / 1000).toFixed(1).replace('.', ',')} km
          {nextAction.leg.status === 'estimated' ? (
            <span className="ml-2">
              <DataBadge status="estimated" label="stima" />
            </span>
          ) : null}
        </p>
      ) : null}

      <div className="mt-3 rounded-[var(--r-md)] bg-surface px-3 py-2">
        <p className="text-[15px] font-semibold text-brand-strong">{nextAction.message}</p>
      </div>

      {item.lat !== null && item.lon !== null ? (
        <Button
          className="mt-3"
          fullWidth
          size="lg"
          iconLeft={<Navigation size={18} />}
          onClick={() => {
            openNavigation(
              { lat: item.lat as number, lon: item.lon as number, label: item.title },
              { from: position, mode: 'walk' },
            );
          }}
        >
          Portami lì
        </Button>
      ) : null}
    </Card>
  );
}

function DayTabs({
  days,
  meteo,
  selectedId,
  onSelect,
}: {
  days: readonly { id: string; date: string; dayIndex: number }[];
  meteo: Record<string, MeteoGiorno>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}): ReactNode {
  return (
    <div
      role="tablist"
      aria-label="Giornate del viaggio"
      className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
    >
      {days.map((day) => {
        const selected = day.id === selectedId;
        const date = new Date(`${day.date}T12:00:00Z`);
        return (
          <button
            key={day.id}
            role="tab"
            aria-selected={selected}
            type="button"
            onClick={() => {
              onSelect(day.id);
            }}
            className={cn(
              'flex min-h-[56px] shrink-0 flex-col items-center justify-center rounded-[var(--r-md)] border px-3',
              selected
                ? 'border-brand bg-brand-soft font-semibold text-brand-strong'
                : 'border-border bg-surface text-text-muted',
            )}
          >
            <span className="text-[12.5px] uppercase">
              {date.toLocaleDateString('it-IT', { weekday: 'short', timeZone: 'UTC' })}
            </span>
            <span className="tabular text-[15px]">
              {date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', timeZone: 'UTC' })}
            </span>
            {/* Il meteo compare solo dove c'è: oltre l'orizzonte di previsione
                la riga resta vuota invece di mostrare un sole inventato. */}
            {meteo[day.date] !== undefined ? (
              <span
                className="tabular text-[12px] text-text-muted"
                title={`${meteo[day.date]?.label ?? ''}`}
              >
                {meteo[day.date]?.icon}{' '}
                {meteo[day.date]?.tempMaxC !== null
                  ? `${String(Math.round(meteo[day.date]?.tempMaxC ?? 0))}°`
                  : ''}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Meteo della giornata aperta.
 *
 * Serve a una decisione concreta: se piove, il museo conviene spostarlo qui e
 * il parco più in là. Per questo l'avviso sulla pioggia compare solo sopra il
 * 40% — sotto quella soglia è rumore che smette di essere letto.
 *
 * Quando la previsione non c'è si dice **perché**: oltre i sedici giorni non
 * esiste previsione, esiste il clima medio, che è un'altra cosa e non aiuta a
 * decidere se portare la giacca.
 */
function MeteoDelGiorno({
  meteo,
  disponibile,
  giorniPrevisione,
}: {
  meteo: MeteoGiorno | undefined;
  disponibile: boolean;
  giorniPrevisione: number;
}): ReactNode {
  if (meteo === undefined) {
    return (
      <p className="mb-3 text-[13.5px] text-text-subtle">
        {disponibile
          ? `Previsione non ancora disponibile: arriva a ${String(giorniPrevisione)} giorni.`
          : 'Meteo non disponibile in questo momento.'}
      </p>
    );
  }

  const pioggia = meteo.rainChance;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] text-text-muted">
      <span aria-hidden="true">{meteo.icon}</span>
      <span className="text-text">{meteo.label}</span>

      {meteo.tempMaxC !== null ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="tabular">
            {String(Math.round(meteo.tempMaxC))}°
            {meteo.tempMinC !== null ? ` / ${String(Math.round(meteo.tempMinC))}°` : ''}
          </span>
        </>
      ) : null}

      {pioggia !== null && pioggia >= 40 ? (
        <span className="rounded-[var(--r-sm)] bg-[var(--budget-warn-soft)] px-2 py-0.5 text-[13px] text-[var(--budget-warn)]">
          Pioggia {String(pioggia)}% — tieni pronto un piano al coperto
        </span>
      ) : null}

      <span className="text-[12px] text-text-subtle">Fonte: Open-Meteo</span>
    </div>
  );
}

function TodaySkeleton(): ReactNode {
  return (
    <div role="status" aria-busy="true" className="flex flex-col gap-3 py-4">
      <span className="sr-only">Caricamento del viaggio</span>
      <div className="h-7 w-40 animate-pulse rounded-md bg-surface-sunken" />
      <div className="h-44 animate-pulse rounded-lg bg-surface-sunken" />
      <div className="h-36 animate-pulse rounded-lg bg-surface-sunken" />
    </div>
  );
}

export type { TripDayDetail };
